
import { defineComponent } from 'vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VModal from '@/components/vendor/modal/VModal.vue';
import ModalHeader from '@/components/vendor/modal/ModalHeader.vue';
import ModalTitle from '@/components/vendor/modal/ModalTitle.vue';
import ModalBody from '@/components/vendor/modal/ModalBody.vue';
import ModalFooter from '@/components/vendor/modal/ModalFooter.vue';
import { mapState } from 'vuex';
import VLoader from '@/components/vendor/VLoader.vue';
import * as yup from 'yup';
import useFormValidation from '@/resources/form';
import { useField } from 'vee-validate';
import useCapabilities from '@/resources/capabilities';
import VFormGroupInputText from '@/components/vendor/basic/form/VFormGroupInputText.vue';
import Professional from '@/api/objects/Professional';
import api from '@/api';
import VFormGroupSelect from '@/components/vendor/basic/form/VFormGroupSelect.vue';
import vSelect from 'vue-select';
import VFormGroupInputDate from '@/components/vendor/basic/form/VFormGroupInputDate.vue';
import VAlert from '@/components/vendor/basic/alert/VAlert.vue';
import VFormGroupInputCheckbox from '@/components/vendor/basic/form/VFormGroupInputCheckbox.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'TheAdminProfessionalUpdate',
  components: {
    VFormGroupInputCheckbox,
    VAlert,
    VFormGroupInputDate,
    VFormGroupSelect,
    VFormGroupInputText,
    VLoader,
    ModalFooter,
    ModalBody,
    ModalTitle,
    ModalHeader,
    VModal,
    VButton,
    vSelect,
  },
  props: {
    professional: {
      type: Professional,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();

    /**
     * I use this method to store variables without
     * numbers, just text.
     *
     * @param number
     */
    // eslint-disable-next-line consistent-return
    const numberToSpelledString = function (number) {
      // TODO: Move to a helper
      switch (number) {
        case 1:
          return 'One';
        case 2:
          return 'Two';
        case 3:
          return 'Three';
        case 4:
          return 'Four';
        case 5:
          return 'Five';
        default:
          return 'One';
      }
    };

    // Dynamically add phone numbers
    const auxRules = {};
    for (let iterator = 1; iterator <= 5; iterator += 1) {
      // Dynamically build each fieldRule
      auxRules[`auxPhoneNumberPrefix${numberToSpelledString(iterator)}`] = yup.lazy((value, options) => {
        let rule = yup.string()
          .label(t('Prefijo telefónico'))
          .nullable();

        const field = options.parent[`auxPhoneNumber${numberToSpelledString(iterator)}`];
        if (!!field && field !== '') {
          rule = rule.required();
        }

        return rule;
      });
      auxRules[`auxPhoneNumber${numberToSpelledString(iterator)}`] = yup.lazy((value, options) => {
        let rule = yup.string()
          .label(t('Número de teléfono'));

        const field = options.parent[`auxPhoneNumberPrefix${numberToSpelledString(iterator)}`];
        if (!!field && field !== '') {
          rule = rule.required()
            .matches(/^\d{6,14}$/, t('Debe tener entre 6 y 14 cifras'));
        }

        return rule;
      });
    }

    const rules = yup.object({
      name: yup.string()
        .required()
        .label(t('Nombre')),
      surname: yup.string()
        .required()
        .label(t('Apellidos')),
      phoneNumberPrefix: yup.string()
        .required()
        .nullable()
        .label(t('Prefijo telefónico')),
      phoneNumber: yup.string()
        .required()
        .label(t('Número de teléfono'))
        .matches(/^\d{6,14}$/, 'Debe tener entre 6 y 14 cifras'),
      genre: yup.string()
        .required()
        .label(t('Género')),
      personalLine1: yup.string()
        .label(t('Dirección 1')),
      personalLine2: yup.string()
        .label(t('Dirección 2')),
      personalCP: yup.string()
        .label(t('Código postal')),
      personalCity: yup.string()
        .label(t('Ciudad')),
      personalProvince: yup.string()
        .label(t('Provincia')),
      personalCountry: yup.string()
        .label(t('País')),
      professionalLine1: yup.string()
        .label(t('Dirección 1')),
      professionalLine2: yup.string()
        .label(t('Dirección 2')),
      professionalCP: yup.string()
        .label(t('Código postal')),
      professionalCity: yup.string()
        .label(t('Ciudad')),
      professionalProvince: yup.string()
        .label(t('Provincia')),
      professionalCountry: yup.string()
        .label(t('País')),
      identityDocType: yup.string()
        .required()
        .label(t('Tipo de Documento')),
      nif: yup.lazy((value, options) => {
        // initialize default rules for all options
        const rule = yup.string()
          .required()
          .label(t('Número de documento'));
        // this property retrieves the information of the datasets initialized with yup
        const { identityDocType } = options.parent;
        const messageValidation = t('El {tipo_documento} registrado no es Válido', { tipo_documento: identityDocType });
        switch (identityDocType) {
          case 'dni':
            return rule.matches(/^[0-9]{8,8}[A-Za-z]$/g, messageValidation);
          case 'nie':
            return rule.matches(/^[XYZ|xyz]\d{7,8}[A-Za-z]$/, messageValidation);
          default:
            return rule; // here you can decide what is the default
        }
      }),
      role: yup.string()
        .required()
        .label(t('Tipo de profesional'))
        .oneOf(['Medico', 'Nutricionista'], t('Debe ser un tipo de profesional permitido')),
      speciality: yup.string()
        .required()
        .label(t('Especialidad'))
        // eslint-disable-next-line max-len
        .oneOf(['Nutricionista', 'Medicina general', 'Medicina interna', 'Endocrinología', 'Dermatología', 'Digestivo', 'Cirugía', 'Medicina estética', 'Medicina integrativa', 'Traumatología', 'Psiquiatría', 'Medicina deportiva', 'Geriatría', 'Neurología'], t('Debe ser una especialidad válida')),
      subspeciality: yup.string()
        .required()
        .label(t('Subespecialidad')),
      college: yup.string()
        .required()
        .label(t('Colegio')),
      collegeNumber: yup.string()
        .required()
        .label(t('Nº colegiado')),
      bornDate: yup.date()
        .required()
        .nullable()
        .typeError(t('Debes introducir une fecha válida'))
        .max(new Date(), t('La fecha de nacimiento no puede ser futura'))
        .label(t('Fecha de nacimiento')),
      email: yup.string()
        .email()
        .required()
        .label(t('Correo electrónico')),
      canFreelyAuthorize: yup.boolean()
        .required()
        .label(t('Permitir autorizar libremente')),
      active: yup.boolean()
        .required()
        .label(t('Activo')),
      ...auxRules,
    });

    const phone = props.professional.phoneNumber.split(' ');

    const form = useFormValidation(rules, {
      name: props.professional.name,
      surname: props.professional.surname,
      phoneNumberPrefix: phone[0],
      phoneNumber: phone[1],
      genre: props.professional.genre,
      identityDocType: props.professional.identityDocType,
      nif: props.professional.nif,
      role: props.professional.user?.role,
      speciality: props.professional.speciality,
      subspeciality: props.professional.subspeciality,
      college: props.professional.college,
      collegeNumber: props.professional.collegeNumber,
      bornDate: props.professional.bornDate,
      email: props.professional.user?.email,
      personalLine1: props.professional.personalLine1,
      personalLine2: props.professional.personalLine2,
      personalCP: props.professional.personalCP,
      personalCity: props.professional.personalCity,
      personalProvince: props.professional.personalProvince,
      personalCountry: props.professional.personalCountry,
      professionalLine1: props.professional.professionalLine1,
      professionalLine2: props.professional.professionalLine2,
      professionalCP: props.professional.professionalCP,
      professionalCity: props.professional.professionalCity,
      professionalProvince: props.professional.professionalProvince,
      professionalCountry: props.professional.professionalCountry,
      canFreelyAuthorize: props.professional.canFreelyAuthorize,
      active: props.professional.active,
    });

    const { value: name } = useField('name');
    const { value: surname } = useField('surname');
    const { value: phoneNumberPrefix } = useField('phoneNumberPrefix');
    const { value: phoneNumber } = useField('phoneNumber');
    const { value: genre } = useField('genre');
    const { value: identityDocType } = useField('identityDocType');
    const { value: nif } = useField('nif');
    const { value: role } = useField('role');
    const { value: speciality } = useField('speciality');
    const { value: subspeciality } = useField('subspeciality');
    const { value: college } = useField('college');
    const { value: collegeNumber } = useField('collegeNumber');
    const { value: bornDate } = useField('bornDate');
    const { value: email } = useField('email');
    const { value: personalLine1 } = useField('personalLine1');
    const { value: personalLine2 } = useField('personalLine2');
    const { value: personalCP } = useField('personalCP');
    const { value: personalCity } = useField('personalCity');
    const { value: personalProvince } = useField('personalProvince');
    const { value: personalCountry } = useField('personalCountry');
    const { value: professionalLine1 } = useField('professionalLine1');
    const { value: professionalLine2 } = useField('professionalLine2');
    const { value: professionalCP } = useField('professionalCP');
    const { value: professionalCity } = useField('professionalCity');
    const { value: professionalProvince } = useField('professionalProvince');
    const { value: professionalCountry } = useField('professionalCountry');
    const { value: active } = useField('active');
    const { value: canFreelyAuthorize } = useField('canFreelyAuthorize');
    // Aux phone numbers
    const { value: auxPhoneNumberPrefixOne } = useField('auxPhoneNumberPrefixOne');
    const { value: auxPhoneNumberOne } = useField('auxPhoneNumberOne');
    const { value: auxPhoneNumberPrefixTwo } = useField('auxPhoneNumberPrefixTwo');
    const { value: auxPhoneNumberTwo } = useField('auxPhoneNumberTwo');
    const { value: auxPhoneNumberPrefixThree } = useField('auxPhoneNumberPrefixThree');
    const { value: auxPhoneNumberThree } = useField('auxPhoneNumberThree');
    const { value: auxPhoneNumberPrefixFour } = useField('auxPhoneNumberPrefixFour');
    const { value: auxPhoneNumberFour } = useField('auxPhoneNumberFour');
    const { value: auxPhoneNumberPrefixFive } = useField('auxPhoneNumberPrefixFive');
    const { value: auxPhoneNumberFive } = useField('auxPhoneNumberFive');

    return {
      ...form,
      name,
      surname,
      phoneNumberPrefix,
      phoneNumber,
      genre,
      identityDocType,
      nif,
      role,
      speciality,
      subspeciality,
      college,
      collegeNumber,
      bornDate,
      email,
      personalLine1,
      personalLine2,
      personalCP,
      personalCity,
      personalProvince,
      personalCountry,
      professionalLine1,
      professionalLine2,
      professionalCP,
      professionalCity,
      professionalProvince,
      professionalCountry,
      active,
      canFreelyAuthorize,
      auxPhoneNumberPrefixOne,
      auxPhoneNumberOne,
      auxPhoneNumberPrefixTwo,
      auxPhoneNumberTwo,
      auxPhoneNumberPrefixThree,
      auxPhoneNumberThree,
      auxPhoneNumberPrefixFour,
      auxPhoneNumberFour,
      auxPhoneNumberPrefixFive,
      auxPhoneNumberFive,

      numberToSpelledString,
      ...useCapabilities(),
    };
  },
  computed: {
    ...mapState('user', ['authUser']),
    ...mapState('app', ['formsSettings']),
    roleOptions() {
      return [
        {
          key: 'Medico',
          label: this.$t('Médico'),
        },
        {
          key: 'Nutricionista',
          label: this.$t('Nutricionista'),
        },
      ];
    },
    specialityOptions() {
      let options = [
        {
          key: 'Nutricionista',
          label: this.$t('Nutricionista'),
        },
      ];

      if (this.role === 'Medico') {
        options = this.formsSettings.medic_speciality_options;
      }

      return options;
    },
    specialityValidValues() {
      const validValues = ['Nutricionista'];

      this.formsSettings.medic_speciality_options.forEach((speciality) => {
        validValues.push(speciality.key);
      });

      return validValues;
    },
  },
  data() {
    return {
      isLoading: true,
    };
  },
  beforeMount() {
    this.auxPhoneNumberPrefixOne = '';
    this.auxPhoneNumberOne = '';
    this.auxPhoneNumberPrefixTwo = '';
    this.auxPhoneNumberTwo = '';
    this.auxPhoneNumberPrefixThree = '';
    this.auxPhoneNumberThree = '';
    this.auxPhoneNumberPrefixFour = '';
    this.auxPhoneNumberFour = '';
    this.auxPhoneNumberPrefixFive = '';
    this.auxPhoneNumberFive = '';

    // Other aux phone numbers
    if (!!this.professional.user?.phoneNumbers) {
      let iterator = 1;
      this.professional.user.phoneNumbers.forEach((auxPhone) => {
        const aux = auxPhone.split(' ');
        // eslint-disable-next-line prefer-destructuring
        this[`auxPhoneNumberPrefix${this.numberToSpelledString(iterator)}`] = aux[0];
        // eslint-disable-next-line prefer-destructuring
        this[`auxPhoneNumber${this.numberToSpelledString(iterator)}`] = aux[1];

        iterator += 1;
      });
    }
  },
  mounted() {
    this.isLoading = false;
  },
  methods: {
    handleCheckboxClick(e, variable) {
      if (e === 'true') {
        this[variable] = true;
      } else if (e === 'false') {
        this[variable] = false;
      }
    },
    parseGenre() {
      // TODO: Please, do this like a computed, it says error when using this.formsSettings (computed also)
      const genre = this.formsSettings.accepted_genre_options.find((ago) => ago.key === this.professional.genre);

      return genre.label;
    },
    async updateProfessional() {
      try {
        const data = {
          name: this.name,
          surname: this.surname,
          phone_number: `${this.values.phoneNumberPrefix} ${this.values.phoneNumber}`,
          genre: this.genre,
          identity_doc_type: this.identityDocType,
          nif: this.nif,
          speciality: this.speciality,
          subspeciality: this.subspeciality,
          college: this.college,
          college_number: this.collegeNumber,
          born_date: this.bornDate,
          role: this.role,
          email: this.email,
          personal_line1: this.personalLine1,
          personal_line2: this.personalLine2,
          personal_cp: this.personalCP,
          personal_city: this.personalCity,
          personal_province: this.personalProvince,
          personal_country: this.personalCountry,
          professional_line1: this.professionalLine1,
          professional_line2: this.professionalLine2,
          professional_cp: this.professionalCP,
          professional_city: this.professionalCity,
          professional_province: this.professionalProvince,
          professional_country: this.professionalCountry,
          can_freely_authorize: this.canFreelyAuthorize,
          active: this.active,
          phone_numbers: [] as string[],
        };

        for (let iterator = 1; iterator <= 5; iterator += 1) {
          // Dynamically build each fieldRule
          if (this.values[`auxPhoneNumberPrefix${this.numberToSpelledString(iterator)}`]
            && this.values[`auxPhoneNumber${this.numberToSpelledString(iterator)}`]) {
            // eslint-disable-next-line no-useless-concat,max-len
            data.phone_numbers.push(`${this.values[`auxPhoneNumberPrefix${this.numberToSpelledString(iterator)}`]} ${this.values[`auxPhoneNumber${this.numberToSpelledString(iterator)}`]}`);
          }
        }

        await api.admin.professional.update(this.professional.id, data);

        this.$toast.success(this.$t('Los datos se han guardado correctamente'));

        this.closeModal();
        this.$emit('professional-updated');
      } catch (e) {
        this.$toast.error(e.response?.data?.message);
      }
    },
    closeModal() {
      this.$emit('closed');
    },
  },
});
